import { ArrowRight, BadgeCheck, Check, Gauge, LineChart, Lock, UserRoundCheck } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { EMAIL_VERIFIED_SIGN_IN_STRINGS } from '@/lib/constants/two-factor-strings';
import { ROUTES } from '@/lib/constants/routes';
import { UI_STRINGS } from '@/lib/constants/ui-strings';
import { journeySignInPath, postAuthLanding } from '@/lib/auth/post-auth-landing';
import { peekPurchaseIntent } from '@/lib/auth/intent-storage';
import { getPendingLogin } from '@/lib/auth/pre-auth';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { SectionCard } from '@/components/common/SectionCard';
import { ShieldNote } from '@/components/common/ShieldNote';
import { IssueReportJourneySteps } from '@/components/common/IssueReportJourneySteps';

const TILES = [
  {
    icon: UserRoundCheck,
    title: UI_STRINGS.EMAIL_VERIFIED.TILE_ACTIVE_TITLE,
    description: UI_STRINGS.EMAIL_VERIFIED.TILE_ACTIVE_DESC,
  },
  {
    icon: Lock,
    title: UI_STRINGS.EMAIL_VERIFIED.TILE_SECURED_TITLE,
    description: UI_STRINGS.EMAIL_VERIFIED.TILE_SECURED_DESC,
  },
  {
    icon: LineChart,
    title: UI_STRINGS.EMAIL_VERIFIED.TILE_GROW_TITLE,
    description: UI_STRINGS.EMAIL_VERIFIED.TILE_GROW_DESC,
  },
] as const;

/** What verification led to: setup with no sign-in, a sign-in first, or a session. */
type VerifiedOutcome = 'setup' | 'signIn' | 'session';

interface NextStep {
  to: string;
  label: string;
  note: string | null;
  subtitle: string;
  /** The three "account active" tiles; the setup step shows none, so the next action leads. */
  showTiles: boolean;
}

/**
 * Where the success CTA goes, and what the page says about it. With 2FA on and
 * registration's sign-in held, authenticator setup is next with no sign-in
 * (FR-217). With 2FA on and the hold gone, the user signs in next and lands on
 * setup (MYN-241). With a session, the dashboard, or the Issue Report journey
 * while a purchase intent is stored (FR-206).
 */
function nextStep(outcome: VerifiedOutcome): NextStep {
  const steps: Record<VerifiedOutcome, () => NextStep> = {
    setup: () => ({
      to: ROUTES.AUTH.TWO_FACTOR_SETUP,
      label: UI_STRINGS.EMAIL_VERIFIED.SETUP_CTA,
      note: UI_STRINGS.EMAIL_VERIFIED.SETUP_NOTE,
      subtitle: UI_STRINGS.EMAIL_VERIFIED.SETUP_SUBTITLE,
      showTiles: false,
    }),
    signIn: () => ({
      to: journeySignInPath(),
      label: EMAIL_VERIFIED_SIGN_IN_STRINGS.CTA,
      note: EMAIL_VERIFIED_SIGN_IN_STRINGS.NOTE,
      subtitle: UI_STRINGS.EMAIL_VERIFIED.SUBTITLE,
      showTiles: true,
    }),
    session: () => ({
      to: postAuthLanding(),
      label: UI_STRINGS.EMAIL_VERIFIED.CONTINUE,
      note: null,
      subtitle: UI_STRINGS.EMAIL_VERIFIED.SUBTITLE,
      showTiles: true,
    }),
  };
  return steps[outcome]();
}

export function VerificationSuccessPage() {
  const state = useLocation().state as { signInRequired?: boolean; setupRequired?: boolean } | null;
  // After a refresh the held login is gone while the history state remains, so
  // setup would bounce to sign-in: say so up front instead.
  const outcome: VerifiedOutcome = state?.setupRequired
    ? getPendingLogin()
      ? 'setup'
      : 'signIn'
    : state?.signInRequired
      ? 'signIn'
      : 'session';
  const next = nextStep(outcome);

  return (
    <div className="flex flex-col items-center bg-page-bg px-4 py-16">
      {peekPurchaseIntent() && <IssueReportJourneySteps stage="secure" className="mb-10" />}
      {/* Concentric success rings */}
      <div className="flex flex-col items-center">
        <div
          aria-hidden="true"
          className="flex h-40 w-40 items-center justify-center rounded-full border-2 border-dashed border-green/30 bg-green/5"
        >
          <div className="flex h-28 w-28 items-center justify-center rounded-full bg-green/15">
            <div className="flex h-20 w-20 items-center justify-center rounded-full bg-dkgreen">
              <Check size={36} strokeWidth={3} className="text-white" />
            </div>
          </div>
        </div>
        <Badge icon={<BadgeCheck size={14} />} className="-mt-3 bg-white shadow-card">
          {UI_STRINGS.EMAIL_VERIFIED.BADGE}
        </Badge>
      </div>

      <h1 className="mt-8 text-center font-sans text-4xl font-bold text-navy sm:text-5xl">
        {UI_STRINGS.EMAIL_VERIFIED.HEADING_PREFIX}
        <span className="text-green">{UI_STRINGS.EMAIL_VERIFIED.HEADING_HIGHLIGHT}</span>
      </h1>
      <p className="mt-4 max-w-md text-center text-subtext">{next.subtitle}</p>

      {next.showTiles && (
        <div className="mt-10 grid w-full max-w-3xl grid-cols-1 gap-5 sm:grid-cols-3">
          {TILES.map(({ icon: Icon, title, description }) => (
            <div
              key={title}
              className="flex flex-col items-center gap-2 rounded-card border border-green/20 bg-green/10 px-6 py-8 text-center"
            >
              <span
                aria-hidden="true"
                className="flex h-10 w-10 items-center justify-center rounded-xl bg-green/20 text-dkgreen"
              >
                <Icon size={18} />
              </span>
              <p className="font-sans text-sm font-semibold text-navy">{title}</p>
              <p className="text-xs text-subtext">{description}</p>
            </div>
          ))}
        </div>
      )}

      <SectionCard className="mt-6 w-full max-w-2xl">
        <div className="flex flex-col items-center gap-5">
          {next.note && <p className="m-0 text-center text-sm text-subtext">{next.note}</p>}
          <Button asChild variant="navy" className="inline-flex items-center gap-2 px-10 py-3.5 text-base drop-shadow-md">
            <Link to={next.to}>
              <Gauge size={18} aria-hidden="true" />
              {next.label}
              <ArrowRight size={18} aria-hidden="true" />
            </Link>
          </Button>
          <ShieldNote>{UI_STRINGS.EMAIL_VERIFIED.ENCRYPTION_NOTE}</ShieldNote>
        </div>
      </SectionCard>
    </div>
  );
}
